import { useMemo } from 'react';
import { useGetVideosQuery, useSearchStoriesQuery } from '@/services/ApiService';
import { StorySourceEnum, type StoryVideoSearchParams } from '@/apiTypes/VideoTypes';
import { toUnifiedVideoItems } from '@/components/Video/unifiedVideoItems';
import { defineHubSection, type HubSectionData } from '@/components/Hub/hubSectionTypes';
import { useHubStoryOccupationId } from '@/components/Hub/useHubStoryOccupationId';
import { storyToUnifiedVideoItem } from '@/components/Hub/hubStoryItems';
import { interleaved } from '@/components/Hub/hubStoryOrder';
import { HubStoryCard } from '@/components/Hub/HubStoryCard';
import { HUB_STORIES_PATH, HUB_STORIES_ROUTE_EXISTS } from '@/components/Hub/hubRoutes';

const MAX_STORIES = 6;

/**
 * What the Hub asks the catalog for away from a career page. The API picks, so the six are drawn
 * from every story rather than from whichever six a stable ordering happens to put first.
 */
const PICKED_FOR_YOU: StoryVideoSearchParams = {
    page: 1,
    limit: MAX_STORIES,
    orderBy: 'random',
    sources: [StorySourceEnum.ROADTRIP_NATION, StorySourceEnum.CAREER_ONE_STOP],
};

const useStoriesData = (): HubSectionData => {
    const occupationId = useHubStoryOccupationId();

    const career = useGetVideosQuery(
        { occupationIds: occupationId ? [occupationId] : [] },
        { skip: !occupationId },
    );

    const catalog = useSearchStoriesQuery(PICKED_FOR_YOU, {
        skip: Boolean(occupationId),
        // The args never change, so a cache hit would pin one random six for the whole session.
        // Closing the Hub unmounts this, which turns remount-refetch into a fresh pick per open.
        refetchOnMountOrArgChange: true,
    });

    const slides = useMemo(() => {
        // A career page shows that career's own stories in the order each source ranked them,
        // alternating so the six-card cap cannot drop a source outright. Anywhere else there is
        // nothing to scope to and the catalog has already picked.
        const items = occupationId
            ? interleaved(
                    toUnifiedVideoItems(career.data?.roadtripNation ?? [], [], [], []),
                    toUnifiedVideoItems([], [], career.data?.careerOneStop ?? [], []),
                )
            : (catalog.data?.items ?? []).map(storyToUnifiedVideoItem);

        return items.slice(0, MAX_STORIES).map(item => ({
            id: item.id,
            render: (isActive: boolean) => <HubStoryCard item={item} isActive={isActive} />,
        }));
    }, [occupationId, career.data?.roadtripNation, career.data?.careerOneStop, catalog.data?.items]);

    const active = occupationId ? career : catalog;

    return {
        slides,
        isLoading: active.isFetching,
        isError: Boolean(active.isError),
    };
};

export const storiesSection = defineHubSection({
    id: 'stories',
    title: 'Stories',
    blurb: 'Watch powerful stories from people like you',
    viewAll: HUB_STORIES_ROUTE_EXISTS ? { to: HUB_STORIES_PATH, label: 'View all' } : null,
    endCard: HUB_STORIES_ROUTE_EXISTS
        ? {
                to: HUB_STORIES_PATH,
                heading: 'Check out some more Stories',
                actionLabel: 'Go to video page',
                groundClassName: 'bg-gz-sage-200',
                zigzagClassName: 'bg-gz-sage-400',
                zigzagOpacityClassName: 'opacity-20',
            }
        : null,
    itemBasisClassName: 'basis-[86%] sm:basis-[82%]',
    surfaceClassName: 'rounded-xl light:bg-gz-surface-dark dark:bg-gz-bluebell-500',
    useData: useStoriesData,
});
